"use client"

import dayjs from "dayjs"
import type { LucideProps } from "lucide-react"
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  Download,
  Filter,
  RefreshCw,
  Search,
} from "lucide-react"
import type React from "react"
import { useEffect, useState } from "react"
import { api } from "../services/api"
import "./TransactionsList.css"

interface Transaction {
  txId: string
  kind: string
  status: string
  network?: string
  networkLogoUrl?: string
  toOnchain?: string
  toOffchain?: string
  concepto?: string
  token: {
    symbol: string
    name: string
    address: string
    logo_url: string
  }
  onchainAmount?: string
  offchainAmount?: string
  receivedAmount?: string
  bankFolio?: string
  txHash?: string
  createdAt: string
  refund?: {
    receivedAmount?: string
    txHashRefund?: string
    bankFolioRefund?: string
    bankFolioURLRefund?: string
    bankHuellaDigitalRefund?: string
  }
}

const statusTranslation: { [key: string]: string } = {
  pending_anchor: "Procesando",
  pending_ramp: "Procesando",
  processing: "Procesando",
  pending_user: "Esperando depósito",
  refunded: "Reembolsado",
  refunding: "Procesando reembolso",
  expired: "Expirada",
  complete: "Completada",
}

const statusIcons: Record<string, React.ComponentType<LucideProps>> = {
  pending_anchor: RefreshCw,
  pending_ramp: RefreshCw,
  processing: RefreshCw,
  pending_user: Clock,
  refunded: CheckCircle,
  refunding: RefreshCw,
  expired: AlertTriangle,
  complete: CheckCircle,
}

const groupByDate = (transactions: Transaction[]) => {
  return transactions.reduce((groups: { [key: string]: Transaction[] }, transaction) => {
    const date = dayjs(transaction.createdAt).format("MMM D, YYYY")
    if (!groups[date]) {
      groups[date] = []
    }
    groups[date].push(transaction)
    return groups
  }, {})
}

const TransactionsList: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [typeFilter, setTypeFilter] = useState("all")

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await api.get("/transactions")
        setTransactions(response.data)
        setFilteredTransactions(response.data)
      } catch (error) {
        console.error("Error fetching transactions:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchTransactions()
  }, [])

  useEffect(() => {
    // Apply filters
    let result = transactions

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      result = result.filter(
        (tx) =>
          tx.txId.toLowerCase().includes(term) ||
          tx.token.symbol.toLowerCase().includes(term) ||
          (tx.network && tx.network.toLowerCase().includes(term)),
      )
    }

    // Status filter
    if (statusFilter !== "all") {
      result = result.filter((tx) => tx.status === statusFilter)
    }

    // Type filter
    if (typeFilter !== "all") {
      result = result.filter((tx) => tx.kind === typeFilter)
    }

    setFilteredTransactions(result)
  }, [searchTerm, statusFilter, typeFilter, transactions])

  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value)
    // You could use a toast notification here instead of an alert
    const notification = document.createElement("div")
    notification.className = "copy-notification"
    notification.textContent = `${label} copiado!`
    document.body.appendChild(notification)

    setTimeout(() => {
      notification.classList.add("show")
      setTimeout(() => {
        notification.classList.remove("show")
        setTimeout(() => {
          document.body.removeChild(notification)
        }, 300)
      }, 1500)
    }, 10)
  }

  const toggleExpand = (txId: string) => {
    const newExpandedIds = new Set(expandedIds)
    if (newExpandedIds.has(txId)) {
      newExpandedIds.delete(txId)
    } else {
      newExpandedIds.add(txId)
    }
    setExpandedIds(newExpandedIds)
  }

  const getStatusIcon = (status: string) => {
    const IconComponent = statusIcons[status] || AlertTriangle
    const isSpinning =
      status === "processing" || status === "pending_anchor" || status === "pending_ramp" || status === "refunding"

    // Create className explicitly
    let iconClassName = "status-icon"
    if (isSpinning) {
      iconClassName = "status-icon spinning"
    }

    return <IconComponent className={iconClassName} />
  }

  const getTransactionCardClassName = (txId: string) => {
    let className = "transaction-card"
    if (expandedIds.has(txId)) {
      className = "transaction-card expanded"
    }
    return className
  }

  const getStatusTextClassName = (status: string) => {
    return "status-text " + status
  }

  const groupedTransactions = groupByDate(filteredTransactions)

  return (
    <div className="transactions-container">
      <div className="transactions-header">
        <h1>Historial de Transacciones</h1>
        <p>Revisa todas tus operaciones y su estado actual</p>
      </div>

      <div className="transactions-filters">
        <div className="search-box">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Buscar por ID, token o red..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-controls">
          <div className="filter-group">
            <Filter className="filter-icon" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
              <option value="all">Todos los estados</option>
              <option value="pending_user">Pendientes</option>
              <option value="processing">Procesando</option>
              <option value="complete">Completadas</option>
              <option value="refunded">Reembolsadas</option>
              <option value="expired">Expiradas</option>
            </select>
          </div>

          <div className="filter-group">
            <Filter className="filter-icon" />
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="filter-select">
              <option value="all">Todos los tipos</option>
              <option value="buy">Compras</option>
              <option value="sell">Ventas</option>
            </select>
          </div>
        </div>
      </div>

      <div className="transactions-list">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Cargando transacciones...</p>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="empty-state">
            <AlertTriangle className="empty-icon" />
            <h3>No se encontraron transacciones</h3>
            <p>Intenta ajustar los filtros o realizar una nueva transacción</p>
          </div>
        ) : (
          Object.entries(groupedTransactions).map(([date, transactions]) => (
            <div key={date} className="transaction-day-group">
              <div className="date-header">
                <Calendar className="date-icon" />
                <h2 className="transaction-date">{date}</h2>
                <span className="transaction-count">{transactions.length} transacciones</span>
              </div>

              {transactions.map((transaction) => (
                <div key={transaction.txId} className={getTransactionCardClassName(transaction.txId)}>
                  <div className="transaction-header" onClick={() => toggleExpand(transaction.txId)}>
                    <div className="transaction-main">
                      <div className="transaction-type">
                        <img
                          src={transaction.networkLogoUrl || "/placeholder.svg?height=30&width=30&query=network"}
                          alt={`${transaction.network} logo`}
                          className="network-logo"
                        />
                        <span className="transaction-kind">{transaction.kind === "buy" ? "Compra" : "Venta"}</span>
                      </div>

                      <div className="transaction-info">
                        {transaction.kind === "buy" ? (
                          <>
                            <div className="token-display">
                              <img src="https://flagcdn.com/w40/mx.png" alt="MXN" className="mxn-logo" />
                              <span className="amount">${transaction.offchainAmount}</span>
                              <span className="currency">MXN</span>
                            </div>
                            <ArrowRight className="arrow-icon" />
                            <div className="token-display">
                              <img
                                src={transaction.token.logo_url || "/placeholder.svg?height=30&width=30&query=token"}
                                alt={`${transaction.token.symbol} logo`}
                                className="token-logo"
                              />
                              <span className="amount">{transaction.onchainAmount}</span>
                              <span className="currency">{transaction.token.symbol}</span>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="token-display">
                              <img
                                src={transaction.token.logo_url || "/placeholder.svg?height=30&width=30&query=token"}
                                alt={`${transaction.token.symbol} logo`}
                                className="token-logo"
                              />
                              <span className="amount">{transaction.onchainAmount}</span>
                              <span className="currency">{transaction.token.symbol}</span>
                            </div>
                            <ArrowRight className="arrow-icon" />
                            <div className="token-display">
                              <img src="https://flagcdn.com/w40/mx.png" alt="MXN" className="mxn-logo" />
                              <span className="amount">${transaction.offchainAmount}</span>
                              <span className="currency">MXN</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="transaction-meta">
                      <div className="status-badge">
                        {getStatusIcon(transaction.status)}
                        <span className={getStatusTextClassName(transaction.status)}>
                          {statusTranslation[transaction.status] || transaction.status}
                        </span>
                      </div>

                      <div className="transaction-time-expand">
                        <span className="transaction-time">{dayjs(transaction.createdAt).format("hh:mm A")}</span>
                        {expandedIds.has(transaction.txId) ? (
                          <ChevronUp className="expand-icon" />
                        ) : (
                          <ChevronDown className="expand-icon" />
                        )}
                      </div>
                    </div>
                  </div>

                  {expandedIds.has(transaction.txId) && (
                    <div className="transaction-details">
                      <div className="detail-row">
                        <div className="detail-label">Yuba ID:</div>
                        <div className="detail-value">
                          <div className="detail-text">{transaction.txId}</div>
                          <button
                            className="copy-button"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleCopy(transaction.txId, "ID")
                            }}
                          >
                            <Copy className="copy-icon" />
                          </button>
                        </div>
                      </div>

                      {transaction.kind === "buy" ? (
                        <>
                          <div className="detail-row">
                            <div className="detail-label">Enviar a:</div>
                            <div className="detail-value">
                              <div className="detail-text">CLABE: {transaction.toOffchain}</div>
                              <button
                                className="copy-button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleCopy(transaction.toOffchain || "", "CLABE")
                                }}
                              >
                                <Copy className="copy-icon" />
                              </button>
                            </div>
                          </div>

                          {transaction.concepto && (
                            <div className="detail-row">
                              <div className="detail-label">Concepto:</div>
                              <div className="detail-value">
                                <div className="detail-text">{transaction.concepto}</div>
                                <button
                                  className="copy-button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCopy(transaction.concepto || "", "Concepto")
                                  }}
                                >
                                  <Copy className="copy-icon" />
                                </button>
                              </div>
                            </div>
                          )}

                          {transaction.toOnchain && (
                            <div className="detail-row">
                              <div className="detail-label">Recibir en:</div>
                              <div className="detail-value">
                                <div className="detail-text address">{transaction.toOnchain}</div>
                                <button
                                  className="copy-button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCopy(transaction.toOnchain || "", "Dirección")
                                  }}
                                >
                                  <Copy className="copy-icon" />
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {transaction.toOnchain && (
                            <div className="detail-row">
                              <div className="detail-label">Enviar a:</div>
                              <div className="detail-value">
                                <div className="detail-text address">{transaction.toOnchain}</div>
                                <button
                                  className="copy-button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCopy(transaction.toOnchain || "", "Dirección")
                                  }}
                                >
                                  <Copy className="copy-icon" />
                                </button>
                              </div>
                            </div>
                          )}

                          {transaction.toOffchain && (
                            <div className="detail-row">
                              <div className="detail-label">Recibir en:</div>
                              <div className="detail-value">
                                <div className="detail-text">{transaction.toOffchain}</div>
                                <button
                                  className="copy-button"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCopy(transaction.toOffchain || "", "Cuenta")
                                  }}
                                >
                                  <Copy className="copy-icon" />
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {transaction.txHash && (
                        <div className="detail-row">
                          <div className="detail-label">Hash de Tx:</div>
                          <div className="detail-value">
                            <div className="detail-text address">{transaction.txHash}</div>
                            <button
                              className="copy-button"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleCopy(transaction.txHash || "", "Hash")
                              }}
                            >
                              <Copy className="copy-icon" />
                            </button>
                          </div>
                        </div>
                      )}

                      {(transaction.status === "refunding" || transaction.status === "refunded") &&
                        transaction.refund && (
                          <div className="refund-section">
                            <h4 className="refund-title">Información de Reembolso</h4>

                            {transaction.refund.receivedAmount && (
                              <div className="detail-row">
                                <div className="detail-label">Monto:</div>
                                <div className="detail-value">
                                  <div className="detail-text">
                                    {transaction.refund.receivedAmount} {transaction.token.symbol}
                                  </div>
                                </div>
                              </div>
                            )}

                            {transaction.refund.txHashRefund && (
                              <div className="detail-row">
                                <div className="detail-label">Hash:</div>
                                <div className="detail-value">
                                  <div className="detail-text address">{transaction.refund.txHashRefund}</div>
                                  <button
                                    className="copy-button"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleCopy(transaction.refund?.txHashRefund || "", "Hash de reembolso")
                                    }}
                                  >
                                    <Copy className="copy-icon" />
                                  </button>
                                </div>
                              </div>
                            )}

                            {transaction.refund.bankFolioRefund && (
                              <div className="detail-row">
                                <div className="detail-label">Folio:</div>
                                <div className="detail-value">
                                  <div className="detail-text">{transaction.refund.bankFolioRefund}</div>
                                  <button
                                    className="copy-button"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleCopy(transaction.refund?.bankFolioRefund || "", "Folio")
                                    }}
                                  >
                                    <Copy className="copy-icon" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {filteredTransactions.length > 0 && (
        <div className="export-section">
          <button className="export-button">
            <Download className="export-icon" />
            Exportar historial
          </button>
        </div>
      )}
    </div>
  )
}

export default TransactionsList
